import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  DIALOGUE_FONT_OPTIONS,
  DIALOGUE_SIZE_OPTIONS,
  findSpeaker,
  resolveHudStyle,
  resolveLineSide,
  resolveMediaUrl,
} from '../lib/dialogue';
import type { DialogueAvatar, DialogueHudStyle, DialogueLine, DialogueScript } from '../types';

interface DialogueHUDProps {
  dialogue: DialogueScript;
  lineId: string | null;
  visible: boolean;
  playing?: boolean;
}

type ChatSlot = {
  id: string;
  exiting: boolean;
};

function bubbleChrome(style: DialogueHudStyle, fontCss: string) {
  return {
    backgroundColor: style.backgroundColor,
    color: style.textColor,
    fontFamily: fontCss,
    borderColor: style.backgroundColor === '#252b29' || style.backgroundColor === '#33474a'
      ? 'rgba(255,255,255,.14)'
      : undefined,
  } as const;
}

function DialogueBubbleView({
  line,
  speaker,
  style,
  fontCss,
  animationClass,
  transitionMs,
  exiting,
  exitAnimation,
}: {
  line: DialogueLine;
  speaker: DialogueAvatar;
  style: DialogueHudStyle;
  fontCss: string;
  animationClass: string;
  transitionMs: number;
  exiting?: boolean;
  exitAnimation?: DialogueHudStyle['chatExitAnimation'];
}) {
  const src = resolveMediaUrl(speaker.image);
  const side = resolveLineSide(line, speaker);
  const hasExitMotion = Boolean(exiting && exitAnimation && exitAnimation !== 'none');
  const exitClass = hasExitMotion ? `exit-${exitAnimation}` : exiting ? 'anim-none' : '';
  const enterClass = exiting ? '' : animationClass;

  return (
    <div
      className={`dialogue-bubble side-${side} ${enterClass} ${exitClass} ${style.showAvatar ? '' : 'no-avatar'}`.trim()}
      style={{
        ...bubbleChrome(style, fontCss),
        animationDuration: transitionMs ? `${transitionMs}ms` : undefined,
      }}
    >
      {style.showAvatar && (
        <div className="dialogue-bubble-avatar">
          {src ? <img src={src} alt="" /> : <span>{speaker.name.slice(0, 1)}</span>}
        </div>
      )}
      <div className="dialogue-bubble-body">
        <strong style={{ color: style.textColor, opacity: 0.72 }}>{speaker.name}</strong>
        <p style={{ color: style.textColor, fontSize: style.fontSize }}>{line.text || '…'}</p>
      </div>
    </div>
  );
}

function visibleChatLineIds(lines: DialogueLine[], lineId: string, maxVisible: number) {
  const index = lines.findIndex((line) => line.id === lineId);
  if (index < 0) return [] as string[];
  const window = lines.slice(0, index + 1);
  if (!maxVisible) return window.map((line) => line.id);
  return window.slice(-maxVisible).map((line) => line.id);
}

function mergeChatSlots(
  previous: ChatSlot[],
  targetIds: string[],
  allowExit: boolean,
  order: Map<string, number>,
): ChatSlot[] {
  const targetSet = new Set(targetIds);
  const next: ChatSlot[] = [];

  for (const slot of previous) {
    if (targetSet.has(slot.id)) continue;
    if (!allowExit) continue;
    next.push({ id: slot.id, exiting: true });
  }

  for (const id of targetIds) {
    next.push({ id, exiting: false });
  }

  return next.sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
}

export function DialogueHUD({ dialogue, lineId, visible, playing = false }: DialogueHUDProps) {
  const style = resolveHudStyle(dialogue.hudStyle);
  const size = DIALOGUE_SIZE_OPTIONS.find((option) => option.id === style.size) ?? DIALOGUE_SIZE_OPTIONS[1];
  const font = DIALOGUE_FONT_OPTIONS.find((option) => option.id === style.fontFamily) ?? DIALOGUE_FONT_OPTIONS[0];
  const [animKey, setAnimKey] = useState(0);
  const [chatSlots, setChatSlots] = useState<ChatSlot[]>([]);
  const [playEpoch, setPlayEpoch] = useState(0);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const exitTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const wasPlayingRef = useRef(false);
  const playRestartRafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!lineId || style.flowMode !== 'subtitle') return;
    setAnimKey((value) => value + 1);
  }, [lineId, style.flowMode]);

  useLayoutEffect(() => {
    const cancelPlayRestart = () => {
      if (playRestartRafRef.current == null) return;
      cancelAnimationFrame(playRestartRafRef.current);
      playRestartRafRef.current = null;
    };

    if (!visible || !lineId || style.flowMode !== 'chat') {
      cancelPlayRestart();
      exitTimers.current.forEach((timer) => clearTimeout(timer));
      exitTimers.current.clear();
      setChatSlots([]);
      wasPlayingRef.current = playing;
      return cancelPlayRestart;
    }

    const targetIds = visibleChatLineIds(dialogue.lines, lineId, style.chatMaxVisible);
    const playJustStarted = playing && !wasPlayingRef.current;
    wasPlayingRef.current = playing;
    const allowExit = style.chatMaxVisible > 0 && style.chatExitAnimation !== 'none';
    const order = new Map(dialogue.lines.map((line, index) => [line.id, index]));

    // Replay/play start: drop every bubble immediately (skip exit anim), then mount fresh.
    if (playJustStarted) {
      cancelPlayRestart();
      exitTimers.current.forEach((timer) => clearTimeout(timer));
      exitTimers.current.clear();
      setPlayEpoch((value) => value + 1);
      setChatSlots([]);
      const restartIds = targetIds;
      playRestartRafRef.current = requestAnimationFrame(() => {
        playRestartRafRef.current = null;
        setChatSlots(restartIds.map((id) => ({ id, exiting: false })));
      });
      return cancelPlayRestart;
    }

    // Playback + edit preview: keep overflow slots as exiting so max-visible cull can animate out.
    setChatSlots((previous) => mergeChatSlots(previous, targetIds, allowExit, order));

    return cancelPlayRestart;
  }, [
    visible,
    lineId,
    playing,
    style.flowMode,
    style.chatMaxVisible,
    style.chatExitAnimation,
    dialogue.lines,
  ]);

  useEffect(() => {
    if (style.flowMode !== 'chat') return;
    const exitMs = Math.max(180, style.transitionMs || 280);
    const exitingIds = new Set(chatSlots.filter((slot) => slot.exiting).map((slot) => slot.id));

    for (const id of exitingIds) {
      if (exitTimers.current.has(id)) continue;
      const timer = setTimeout(() => {
        exitTimers.current.delete(id);
        setChatSlots((current) => current.filter((slot) => slot.id !== id));
      }, exitMs);
      exitTimers.current.set(id, timer);
    }

    for (const [id, timer] of [...exitTimers.current.entries()]) {
      if (exitingIds.has(id)) continue;
      clearTimeout(timer);
      exitTimers.current.delete(id);
    }
  }, [chatSlots, style.flowMode, style.transitionMs]);

  useEffect(() => {
    if (style.flowMode !== 'chat') return;
    const node = chatScrollRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [chatSlots, style.flowMode, lineId]);

  useEffect(() => () => {
    if (playRestartRafRef.current != null) {
      cancelAnimationFrame(playRestartRafRef.current);
      playRestartRafRef.current = null;
    }
    exitTimers.current.forEach((timer) => clearTimeout(timer));
    exitTimers.current.clear();
  }, []);

  if (!visible || !lineId) return null;

  const maxWidth = `min(${size.width}px, calc(100% - 40px))`;
  const enterMs = style.animation === 'none' ? 0 : style.transitionMs;
  const exitMs = Math.max(180, style.transitionMs || 280);
  const enterClass = style.animation === 'none' ? 'anim-none' : `anim-${style.animation}`;

  if (style.flowMode === 'subtitle') {
    const line = dialogue.lines.find((item) => item.id === lineId) ?? null;
    const speaker = line ? findSpeaker(dialogue, line.speakerId) : null;
    if (!line || !speaker) return null;
    const side = resolveLineSide(line, speaker);

    return (
      <div
        className={`dialogue-hud mode-subtitle side-${side} size-${style.size}`}
        style={{ maxWidth }}
        aria-live="polite"
      >
        <DialogueBubbleView
          key={animKey}
          line={line}
          speaker={speaker}
          style={style}
          fontCss={font.css}
          animationClass={enterClass}
          transitionMs={enterMs}
        />
      </div>
    );
  }

  return (
    <div
      className={`dialogue-hud mode-chat size-${style.size}`}
      style={{ maxWidth, width: `min(${size.width}px, calc(100% - 40px))` }}
      aria-live="polite"
    >
      <div className="dialogue-hud-chat-scroll" ref={chatScrollRef}>
        {chatSlots.map((slot) => {
          const line = dialogue.lines.find((item) => item.id === slot.id);
          const speaker = line ? findSpeaker(dialogue, line.speakerId) : null;
          if (!line || !speaker) return null;
          return (
            <DialogueBubbleView
              key={`${playEpoch}:${slot.id}${slot.exiting ? ':exit' : ''}`}
              line={line}
              speaker={speaker}
              style={style}
              fontCss={font.css}
              animationClass={enterClass}
              transitionMs={slot.exiting ? exitMs : enterMs}
              exiting={slot.exiting}
              exitAnimation={style.chatExitAnimation}
            />
          );
        })}
      </div>
    </div>
  );
}
